import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import * as React from "react";

import { RobotoLinkHoverUnderline } from "@/shared/components/RobotoLinkHoverUnderline";
import { basename } from "@/shared/domain/files/filesystem";
import { useFile } from "@/shared/domain/files/hooks/useFile.ts";
import { Goto } from "@/shared/navigation/Goto";
import { LoggerService } from "@/shared/services/LoggerService";

interface FileVizLinkProps {
  fileId: string;

  /**
   * If provided, the file will be opened at the given time.
   * Otherwise, the file will be opened at the start of the file.
   *
   * `startTime` is in nanoseconds since the UNIX epoch.
   **/
  startTime?: bigint;

  /**
   * Optional text content to display below the link
   **/
  subtitle?: React.ReactNode;
}

export const FileVizLink: React.FC<FileVizLinkProps> = ({
  fileId,
  startTime,
  subtitle,
}) => {
  const fileQuery = useFile(fileId);

  if (fileQuery.isLoading) {
    return (
      <Typography variant="body2">
        <Skeleton variant="text" width="200px" />
      </Typography>
    );
  }

  if (fileQuery.isError || fileQuery.data === undefined) {
    LoggerService.error(fileQuery.error?.message, fileQuery.error);
    return (
      <Typography variant="body2" color="error">
        Failed to load event scope
      </Typography>
    );
  }

  const link = (
    <RobotoLinkHoverUnderline
      to={Goto.createFileVisualizerRoute(fileId, startTime)}
      variant="body2"
      title={fileQuery.data.relative_path}
    >
      {basename(fileQuery.data.relative_path)}
    </RobotoLinkHoverUnderline>
  );

  if (subtitle) {
    return (
      <>
        {link}
        {subtitle}
      </>
    );
  }

  return link;
};
